import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

/**
 * Campaign media — the images, PDFs and videos sent in a WhatsApp template's media header.
 *
 * The plain upload mutation sends FormData rather than JSON. fetchBaseQuery leaves FormData alone
 * (it neither stringifies it nor sets Content-Type), which is what lets the browser add the
 * multipart boundary, so no special handling is needed there. For a real progress bar, though,
 * fetch gives no upload-progress signal at all — that's what uploadWaMediaWithProgress (below,
 * outside injectEndpoints) is for, via plain XMLHttpRequest.
 */
export const whatsappMediaApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listWaMedia: build.query({
      query: (filter) => `/whatsapp-media${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      providesTags: (result) => [
        { type: 'WaMedia', id: 'LIST' },
        ...(result?.items?.map((m) => ({ type: 'WaMedia', id: m.id })) ?? []),
      ],
    }),
    uploadWaMedia: build.mutation({
      query: (file) => {
        const body = new FormData();
        body.append('file', file);
        return { url: '/whatsapp-media', method: 'POST', body };
      },
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'WaMedia', id: 'LIST' }],
    }),
    updateWaMedia: build.mutation({
      query: ({ id, ...body }) => ({ url: `/whatsapp-media/${id}`, method: 'PATCH', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'WaMedia', id: 'LIST' }, { type: 'WaMedia', id }],
    }),
    deleteWaMedia: build.mutation({
      query: (id) => ({ url: `/whatsapp-media/${id}`, method: 'DELETE' }),
      // Automations reference media, so a successful delete can change what they display.
      invalidatesTags: [{ type: 'WaMedia', id: 'LIST' }, 'Automation'],
    }),
  }),
});

export const {
  useListWaMediaQuery,
  useLazyListWaMediaQuery,
  useUploadWaMediaMutation,
  useUpdateWaMediaMutation,
  useDeleteWaMediaMutation,
} = whatsappMediaApi;

const API_BASE = () => (process.env.REACT_APP_API_URL
  || 'https://healingapi.icygrass-68e73490.centralindia.azurecontainerapps.io/api').replace(/\/$/, '');

/**
 * Absolute URL of an asset's bytes — the same anonymous endpoint Meta fetches, reused here for
 * thumbnails and native <video> previews.
 */
export const waMediaContentUrl = (id) => `${API_BASE()}/whatsapp-media/${id}/content`;

/**
 * Uploads with real byte-level progress, via XMLHttpRequest — fetch (what RTK Query's
 * fetchBaseQuery uses) has no upload-progress event at all, only download progress, so this
 * deliberately steps outside RTK Query for this one call. Returns a promise resolving to the
 * created asset; call `onProgress(0-100)` as it goes.
 *
 * @param {File} file
 * @param {string} token  current access token, since this bypasses baseApi's prepareHeaders
 * @param {(pct: number) => void} onProgress
 */
export function uploadWaMediaWithProgress(file, token, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE()}/whatsapp-media`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body;
      try { body = JSON.parse(xhr.responseText); } catch { body = null; }
      if (xhr.status >= 200 && xhr.status < 300 && body?.success) resolve(body.data);
      else reject(body?.error || { message: `Upload failed (HTTP ${xhr.status}).` });
    };
    xhr.onerror = () => reject({ message: 'Upload failed — network error.' });

    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });
}
